import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnChanges,
  AfterViewInit,
  HostListener,
  SimpleChanges,
} from '@angular/core';
import { OmniChatFacade } from '@cigna/omni/chat-state-data-access';
import { OmniConversationsFacade } from '@cigna/omni/conversations-state-data-access';
import { take } from 'rxjs/operators';
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';

@Component({
  selector: 'cigna-omni-shell-prechat-header',
  templateUrl: './chat-header.component.html',
  styleUrls: ['./chat-header.component.scss'],
})
export class OmniShellChatHeaderComponent implements OnChanges, AfterViewInit {
  @ViewChild('headerTitle')
  headerTitle: ElementRef;

  public isBackButtonDisabled$ = this.facade.isBackButtonDisabled$;
  public title$ = this.facade.headerTitle$;
  public headerBackgroundClass$ = this.facade.headerBackgroundClass$;
  public isUnmuted$ = this.facade.isUnmuted$;
  public toggleButton$ = this.facade.toggleChatStateButton$;
  public currentComponent$ = this.facade.currentComponent$;
  public shouldCloseOrExit$ = this.conversationsFacade.closeOrExitFlag$;
  public conversation$ = this.conversationsFacade.conversation$;
  public showOptionsMenu$ = this.facade.showOptionsMenu$;
  public canGoBack$ = this.facade.canGoBack$;
  public enableChatShellDrag$ = this.features.featuresEnabled([
    'moveableChatWindow',
  ]);

  private menuItems: ElementRef[] = [];
  menuStatusMessage = '';

  @Input() public isHistory: boolean;
  @Input() public existingConvId: string;
  @Input() public shouldCloseSplashScreen: boolean;
  @Input() public shouldFocusOnHeader: boolean;
  @Input() public isDcmChat: boolean;
  @Input() public canEnableNewDMUI: boolean;
  @Input() shouldEnableChatShellDrag = false;

  @Output() public printEmitter: EventEmitter<void> = new EventEmitter();
  @Output() public closeChat: EventEmitter<void> = new EventEmitter();
  @ViewChild('chatHeader') chatHeader: ElementRef<HTMLElement>;
  @ViewChild('muteButton') muteButton?: ElementRef;
  @ViewChild('unmuteButton') unmuteButton?: ElementRef;
  @ViewChild('transcriptButton') transcriptButton?: ElementRef;

  isMenuOpen = false;

  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent) {
    const e = event.target as HTMLElement;

    // We do not want to close the menu if the menu icon is clicked
    if (e?.parentElement?.parentElement?.parentElement?.id !== 'menu_icon') {
      this.isMenuOpen = false;
    }
  }

  public toggleMute() {
    this.facade.toggleMuteState();
  }

  constructor(
    public facade: OmniChatFacade,
    public conversationsFacade: OmniConversationsFacade,
    private features: FeatureTogglesFacade,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.shouldFocusOnHeader?.currentValue) {
      this.headerTitle.nativeElement.focus();
      this.conversationsFacade.setHeaderFocus(false);
    }
  }

  ngAfterViewInit() {
    this.headerTitle.nativeElement.focus();
    this.updateMenuItems();
  }

  public backToDialog(): void {
    if (this.isHistory) {
      this.conversationsFacade.setConversationId(this.existingConvId);
    }
    this.conversation$.pipe(take(1)).subscribe((conversation) => {
      if (
        !this.isHistory &&
        this.canEnableNewDMUI &&
        conversation?.type === 'async'
      ) {
        this.conversationsFacade.setConversationId('');
      }
    });
    this.facade.back();
    this.headerTitle.nativeElement.focus();
  }

  public toggleChatState(): void {
    this.facade.toggleChatState();
  }

  public closeOrEndChat(close: boolean): void {
    if (close) {
      if (this.existingConvId) {
        this.conversationsFacade.endConversation(this.existingConvId);
      }
      this.conversationsFacade.setConversationId('');
      this.facade.closeDialog();
      this.closeChat.emit();

      setTimeout(() => {
        this.focusChatBubble();
      }, 300);
    } else {
      this.conversationsFacade.setCloseSplashScreen(true);
    }
  }

  private focusChatBubble(): void {
    const ariaLabel = 'aria-label';
    const chatBubbleButton = document.querySelector(
      '.conv-chat-bubble-button-expander, .conv-chat-bubble-button',
    ) as HTMLElement | null;

    if (chatBubbleButton) {
      chatBubbleButton.focus();

      const originalAriaLabel = chatBubbleButton.getAttribute(ariaLabel);
      chatBubbleButton.setAttribute(
        ariaLabel,
        'Chat window closed. Chat with us',
      );

      this.announceToScreenReader('Returning to contact page.');

      setTimeout(() => {
        if (originalAriaLabel) {
          chatBubbleButton.setAttribute(ariaLabel, originalAriaLabel);
        }
      }, 1500);
    }
  }

  private announceToScreenReader(message: string): void {
    let liveRegion = document.getElementById('chat-status-announcer');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'chat-status-announcer';
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = '';

    setTimeout(() => {
      if (liveRegion) {
        liveRegion.textContent = message;

        setTimeout(() => {
          if (liveRegion) {
            liveRegion.textContent = '';
          }
        }, 3000);
      }
    }, 100);
  }

  public focusOnChatHeader() {
    this.headerTitle.nativeElement.focus();
  }

  private updateMenuItems() {
    this.menuItems = [
      this.muteButton || this.unmuteButton,
      this.transcriptButton,
    ].filter((item): item is ElementRef => !!item);
  }

  private openMenuAndFocusFirst() {
    this.isMenuOpen = true;
    this.menuStatusMessage = 'Menu expanded';
    setTimeout(() => {
      this.updateMenuItems();
      if (this.menuItems[0]) {
        this.menuItems[0].nativeElement.focus();
      }
    });

    setTimeout(() => {
      this.menuStatusMessage = '';
    }, 1000);
  }

  public toggleMenu() {
    if (!this.isMenuOpen) {
      this.openMenuAndFocusFirst();
    } else {
      this.isMenuOpen = false;
      this.menuStatusMessage = 'Menu collapsed';
      setTimeout(() => {
        document.getElementById('menu_icon')?.focus();
      });

      setTimeout(() => {
        this.menuStatusMessage = '';
      }, 1000);
    }
  }

  public onMenuButtonKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.altKey && event.code === 'Space') {
      event.preventDefault();
      this.toggleMenu();
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          this.toggleMenu();
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.openMenuAndFocusFirst();
        break;
    }
  }

  public onMenuKeydown(event: KeyboardEvent) {
    if (!this.isMenuOpen) {
      return;
    }

    const focusedElement = document.activeElement as HTMLElement;
    const currentIndex = this.menuItems.findIndex(
      (item) => item.nativeElement === focusedElement,
    );

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.isMenuOpen = false;
        this.menuStatusMessage = 'Menu collapsed';
        document.getElementById('menu_icon')?.focus();
        setTimeout(() => {
          this.menuStatusMessage = '';
        }, 1000);
        break;

      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % this.menuItems.length;
        this.menuItems[nextIndex]?.nativeElement.focus();
        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        const prevIndex =
          currentIndex <= 0 ? this.menuItems.length - 1 : currentIndex - 1;
        this.menuItems[prevIndex]?.nativeElement.focus();
        break;
      }

      case 'Home':
        event.preventDefault();
        this.menuItems[0]?.nativeElement.focus();
        break;

      case 'End':
        event.preventDefault();
        this.menuItems[this.menuItems.length - 1]?.nativeElement.focus();
        break;
    }
  }

  public selectMenuItem(action: string) {
    switch (action) {
      case 'mute':
      case 'unmute':
        this.toggleMute();
        break;
      case 'transcript':
        this.printEmitter.emit();
        break;
    }
    this.isMenuOpen = false;
    document.getElementById('menu_icon')?.focus();
  }
}
